import { useCallback } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { groupItemsBySegmentAndSeriesForPDF } from "@/utils/pdfDataUtils";
import {
  formatCurrency,
  calculateInstallments,
  resolveInstallmentInfo,
} from "@/lib/formatters";
import { conexiaImage } from "@/assets/conexia-image";
import { toast } from "@/components/ui/sonner";
import { isCouponItemsEnabled, COUPON_ITEM_DISCOUNT_AS_VALUE } from "@/features/couponItemsFeature";
import { applyCouponDiscountToPdfItems, type PdfCouponItem } from "@/lib/couponItems";
import { isCustomerGroupAddressEnabled } from "@/features/customerGroupAddressFeature";
import { addressSingleLine, type AddressLinesSource } from "@/components/shared/address/addressFormat";

interface OrderItem {
  id: number;
  codigo_produto: string;
  descricao_produto: string;
  preco: number;
  ano_letivo: number;
  marca: string;
  sku: string;
  status: string;
  utilizacao?: string | null;
  serie?: string | null;
  segmento?: string | null;
  quantidade_contratual: number;
  desconto: number;
  quantidade_pedido: number;
  valor_liquido: number;
  valor_total: number;
  delivery_type_id: number;
  delivery_type_number: number;
  brand_id: number;
}

export interface NativePDFOptions {
  filename: string;
  orderNumber: string;
  clientName: string;
  clientCode: string;
  clientCNPJ: string;
  clientAddress: string;
  clientNumber: string;
  clientDistrict: string;
  clientCity: string;
  clientState: string;
  userEmail: string;
  quantityInstallment?: number;
  annualQuantityInstallment?: number;
  semestralQuantityInstallment?: number;
  clientPostCode: string;
  /**
   * Endereços do pedido (customerGroupAddressFeature): o de faturamento é o padrão de cobrança da escola e o de
   * entrega, o escolhido no pedido. Sem faturamento vale o endereço do cadastro (clientAddress...); sem entrega
   * (pedidos antigos), o de faturamento. Com a flag desligada o PDF mostra só o endereço do cadastro.
   */
  billingAddress?: AddressLinesSource | null;
  shippingAddress?: AddressLinesSource | null;
  orderItems: OrderItem[];
  grossSubtotal: number;
  netSubtotal: number;
  total: number;
  shippingTotal?: number;
  /** Desconto do cupom já abatido do total; as parcelas são calculadas depois dele. */
  couponDiscount?: number;
  /** Cupom por itens: itens bonificados (página informativa no PDF) e código do cupom. */
  couponCode?: string;
  couponItems?: PdfCouponItem[];
  createdBy?: string;
  date?: string;
  isScheduled?: boolean;
}

// Extend jsPDF type to include autoTable properties
interface ExtendedJsPDF extends jsPDF {
  lastAutoTable?: {
    finalY: number;
  };
}

type Colors = { [key: string]: [number, number, number] };

const conexiaColors: Colors = {
  primary: [81, 190, 174],
  primaryBackground: [238, 249, 247],
  secondary: [68, 154, 178],
  secondaryBackground: [236, 245, 247],
  aux: [25, 71, 122],
  auxBackground: [232, 237, 242],
};

export const useNativePDFGenerator = () => {

  const generateNativePDF = useCallback(
    async (options: NativePDFOptions) => {
      try {

        const {
          filename,
          orderNumber,
          clientName,
          clientCode,
          clientCNPJ,
          clientAddress,
          clientNumber,
          clientDistrict,
          clientCity,
          clientState,
          clientPostCode,
          orderItems: rawOrderItems,
          grossSubtotal,
          netSubtotal,
          total,
          shippingTotal,
          couponDiscount: rawCouponDiscount,
          userEmail,
          quantityInstallment,
          createdBy,
          isScheduled,
          date
        } = options;

        // Cupom por itens (isolado em @/lib/couponItems e na flag COUPON_ITEM_DISCOUNT_AS_VALUE): com a flag
        // ligada o desconto vai no valor do próprio item e some a linha "Desconto do cupom"; desligada, o PDF
        // volta ao comportamento anterior (itens brutos + desconto em linha separada).
        const couponItems = isCouponItemsEnabled ? (options.couponItems ?? []) : [];
        const couponDiscountAsValue = couponItems.length > 0 && COUPON_ITEM_DISCOUNT_AS_VALUE;
        const orderItems = couponDiscountAsValue
          ? applyCouponDiscountToPdfItems(rawOrderItems, couponItems)
          : rawOrderItems;
        const couponDiscount = couponDiscountAsValue ? 0 : rawCouponDiscount;

        // Create PDF instance with extended type
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        }) as ExtendedJsPDF;

        // Page 1 - Summary
        generateSummaryPage(pdf, {
          date,
          orderNumber,
          clientName,
          clientCode,
          clientCNPJ,
          clientAddress,
          clientNumber,
          clientDistrict,
          clientCity,
          clientState,
          clientPostCode,
          billingAddress: options.billingAddress,
          shippingAddress: options.shippingAddress,
          grossSubtotal,
          netSubtotal,
          userEmail,
          total,
          shippingTotal,
          couponDiscount,
          couponCode: options.couponCode,
          couponItems,
          orderItems,
          quantityInstallment,
          createdBy,
          isScheduled,
        });

        // Cupom por itens: unidades cobertas por código do produto (a descrição se repete entre séries), para a coluna "Coberto por cupom".
        const couponQtyByName = new Map<string, number>();
        couponItems.filter((item) => item.quantity > 0).forEach((item) => {
          couponQtyByName.set(item.productCode, (couponQtyByName.get(item.productCode) ?? 0) + item.quantity);
        });

        // Get grouped data from utility
        const segmentGroups = groupItemsBySegmentAndSeriesForPDF(orderItems);

        // Generate detailed pages
        segmentGroups.forEach((segment, index) => {
          pdf.addPage();
          generateSegmentPage(pdf, {
            segment,
            segmentIndex: index,
            totalSegments: segmentGroups.length,
            orderNumber,
            clientName,
            segmentGroups, // Passando todos os segmentos para calcular total geral
            couponQtyByName,
          });
        });

        // Return PDF blob
        const pdfBlob = pdf.output("blob");

        return pdfBlob;
      } catch (error) {
        console.error("Error generating native PDF:", error);
        toast.error("Erro ao gerar PDF nativo", {
          description: "Ocorreu um erro inesperado durante a criação do documento.",
        });
        return false;
      }
    },
    [toast]
  );

  return { generateNativePDF };
};

// Helper functions

/**
 * Linhas de endereço de "Dados do Cliente". Com customerGroupAddressFeature: faturamento (padrão de cobrança, ou o
 * endereço do cadastro) e entrega (a do pedido, ou o de faturamento). Desligada, o endereço do cadastro como antes.
 */
type ClientAddressData = Pick<
  NativePDFOptions,
  | "clientAddress"
  | "clientNumber"
  | "clientDistrict"
  | "clientCity"
  | "clientState"
  | "clientPostCode"
  | "billingAddress"
  | "shippingAddress"
>;

/** Linha de "Dados do Cliente"; com rótulo, ele vai em negrito numa linha e o valor na linha de baixo. */
interface ClientRow {
  label?: string;
  value: string;
}

function clientAddressRows(data: ClientAddressData): ClientRow[] {
  if (!isCustomerGroupAddressEnabled) {
    return [{
      value: `Endereço: ${data.clientAddress}, ${data.clientNumber} - ${data.clientDistrict}, ${data.clientCity} - ${data.clientState}, ${data.clientPostCode}`,
    }];
  }

  const registrationAddress: AddressLinesSource = {
    street: data.clientAddress ?? "",
    number: data.clientNumber,
    complement: null,
    district: data.clientDistrict,
    city: data.clientCity ?? "",
    state: data.clientState ?? "",
    postcode: data.clientPostCode ?? "",
  };
  const billing: AddressLinesSource = data.billingAddress ?? registrationAddress;
  const shipping: AddressLinesSource = data.shippingAddress ?? billing;

  return [
    { label: "Endereço de faturamento:", value: addressSingleLine(billing) || "-" },
    { label: "Endereço de entrega:", value: addressSingleLine(shipping) || "-" },
  ];
}

function generateSummaryPage(pdf: ExtendedJsPDF, data: any) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  let yPosition = 20;

  // Financial details - Update to use new nomenclature
  const getProductsByDeliveryType = (deliveryTypeNumber: number) => {
    return data.orderItems
      .filter((item: OrderItem) => {
        if (item.delivery_type_number !== deliveryTypeNumber || item.quantidade_pedido <= 0) {
          return false;
        }

        // Exclude "Plataforma AZ - 2º Semestre" from semestral products
        // if (
        //   deliveryTypeNumber === 2 &&
        //   item.descricao_produto.includes( "Plataforma AZ" ) &&
        //   item.descricao_produto.includes( "2º Semestre" )
        // ) {
        //   return false;
        // }

        return true;
      })
      .reduce((total: number, item: OrderItem) => total + item.valor_total, 0);
  };

  const produtosAnuaisTotal = getProductsByDeliveryType(1);
  const produtosSemestraisTotal = getProductsByDeliveryType(2);
  const totalItensOutros = data.orderItems
    .filter((item: OrderItem) => item.delivery_type_number !== 1 && item.delivery_type_number !== 2 && item.quantidade_pedido > 0)
    .reduce((total: number, item: OrderItem) => total + item.valor_total, 0);

  let orderType = "Pedido de Compra";

  if (data.isScheduled) {
    orderType = "Pedido de 2º Semestre";
  } else if (data.orderNumber.includes("-S") || data.orderNumber.includes("PED-S") || (produtosSemestraisTotal > 0 && produtosAnuaisTotal === 0)) {
    orderType = "Pedido Semestral";
  } else if (data.orderNumber.includes("-A") || data.orderNumber.includes("PED-A") || (produtosAnuaisTotal > 0 && produtosSemestraisTotal === 0)) {
    orderType = "Pedido Anual";
  } else if (produtosAnuaisTotal > 0 && produtosSemestraisTotal > 0) {
    orderType = "Pedido Anual e Semestral";
  } else {
    orderType = data.orderNumber.split('-')[1] === 'A' ? "Pedido Anual" : "Pedido Semestral";
  }

  // Header
  pdf.addImage(conexiaImage, "PNG", 14, 10, 40, 12);
  yPosition += 12;
  pdf.setFontSize(24);
  pdf.setFont("helvetica", "bold");
  pdf.text("PEDIDO DE COMPRA", pageWidth / 2, yPosition, { align: "center" });

  yPosition += 10;
  pdf.setFontSize(18);
  pdf.setTextColor(...conexiaColors.secondary);
  pdf.text(`#${data.orderNumber}`, pageWidth / 2, yPosition, {
    align: "center",
  });

  yPosition += 8;
  pdf.setFontSize(12);
  pdf.setTextColor(100, 100, 100);
  pdf.text(orderType, pageWidth / 2, yPosition, {
    align: "center",
  });

  const getSafeDate = () => {
    const d = new Date(data.date);
    // Se a data for inválida ou anterior a 2020, tenta forçar o timestamp atual
    if (isNaN(d.getTime()) || d.getFullYear() < 2020) {
      return new Date(Date.now());
    }
    return d;
  };

  const safeNow = getSafeDate();
  const day = String(safeNow.getDate()).padStart(2, '0');
  const month = String(safeNow.getMonth() + 1).padStart(2, '0');
  const year = safeNow.getFullYear();
  const hours = String(safeNow.getHours()).padStart(2, '0');
  const minutes = String(safeNow.getMinutes()).padStart(2, '0');
  const seconds = String(safeNow.getSeconds()).padStart(2, '0');

  const formattedDate = `${day}/${month}/${year}`;
  const formattedTime = `${hours}:${minutes}:${seconds}`;

  yPosition += 8;
  pdf.setFontSize(12);
  pdf.setTextColor(100, 100, 100);
  pdf.text(`Gerado em: ${formattedDate} às ${formattedTime}`, pageWidth / 2, yPosition, { align: "center" });

  yPosition += 3;
  pdf.setFontSize(9);
  pdf.text(`Criado por: ${data.createdBy || data.userEmail || '-'}`, pageWidth / 2, yPosition, { align: "center" });

  yPosition += 7;

  // Client Information Box
  pdf.setFontSize(11);
  pdf.setFont("helvetica", "normal");
  const clientRows = [
    { value: `Nome: ${data.clientName}` },
    { value: `CNPJ: ${data.clientCNPJ}` },
    ...clientAddressRows(data),
  ].map((row: ClientRow) => ({ label: row.label, lines: pdf.splitTextToSize(row.value, pageWidth - 50) as string[] }));
  // 6 mm entre um dado e outro; dentro do dado (rótulo e endereço longo), a altura de linha da fonte.
  const wrappedLineHeight = (pdf.getFontSize() * pdf.getLineHeightFactor()) / pdf.internal.scaleFactor;
  const clientRowsY: number[] = [];
  let clientRowY = yPosition + 12;
  clientRows.forEach((row) => {
    clientRowsY.push(clientRowY);
    clientRowY += ((row.label ? 1 : 0) + row.lines.length - 1) * wrappedLineHeight + 6;
  });
  const clientBoxBottom = clientRowY;

  pdf.setFillColor(248, 249, 250);
  pdf.rect(14, yPosition - 5, pageWidth - 28, clientBoxBottom - (yPosition - 5), "F");

  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(14);
  pdf.setFont("helvetica", "bold");
  pdf.text("Dados do Cliente", 25, yPosition + 5);

  pdf.setFontSize(11);
  clientRows.forEach((row, index) => {
    let rowY = clientRowsY[index];
    if (row.label) {
      pdf.setFont("helvetica", "bold");
      pdf.text(row.label, 25, rowY);
      rowY += wrappedLineHeight;
    }
    pdf.setFont("helvetica", "normal");
    pdf.text(row.lines, 25, rowY);
  });

  yPosition = clientBoxBottom + 10;

  // Financial Summary Box
  pdf.setFillColor(248, 249, 250);
  const couponDiscount: number = Number(data.couponDiscount) > 0 ? Number(data.couponDiscount) : 0;
  pdf.rect(14, yPosition - 5, pageWidth - 28, couponDiscount > 0 ? 41 : 35, "F");

  pdf.setFontSize(14);
  pdf.setFont("helvetica", "bold");
  pdf.text("Resumo Financeiro", 25, yPosition + 5);

  pdf.setFontSize(11);
  pdf.setFont("helvetica", "normal");

  const financialDetails: { label: string; value: string }[] = [];

  if (data.isScheduled) {
    financialDetails.push({
      label: "Pedido de 2º Semestre",
      value: formatCurrency(data.netSubtotal),
    });
  } else {
    if (produtosAnuaisTotal > 0) {
      financialDetails.push({
        label: "Pedido Anual",
        value: formatCurrency(produtosAnuaisTotal),
      });
    }
    if (produtosSemestraisTotal > 0) {
      financialDetails.push({
        label: "Pedido Semestral",
        value: formatCurrency(produtosSemestraisTotal),
      });
    }
    if (totalItensOutros > 0) {
      financialDetails.push({
        label: "Outros Itens",
        value: formatCurrency(totalItensOutros),
      });
    }
  }

  if (financialDetails.length === 0) {
    financialDetails.push({
      label: "Subtotal",
      value: formatCurrency(data.netSubtotal),
    });
  }

  const frete = data.shippingTotal ? data.shippingTotal : 0;

  let detailY = yPosition + 12;
  financialDetails.forEach((detail) => {
    pdf.text(detail.label, 25, detailY);
    pdf.text(detail.value, pageWidth - 25, detailY, { align: "right" });
    detailY += 6;
  });

  if (frete > 0) {
    pdf.text("Frete", 25, detailY);
    pdf.text(formatCurrency(frete), pageWidth - 25, detailY, { align: "right" });
    detailY += 6;
  }

  if (couponDiscount > 0) {
    pdf.text("Desconto do cupom", 25, detailY);
    pdf.text(`- ${formatCurrency(couponDiscount)}`, pageWidth - 25, detailY, { align: "right" });
    detailY += 6;
  }

  // Total
  yPosition += couponDiscount > 0 ? 38 : 32;
  pdf.setFillColor(...conexiaColors.primaryBackground);
  pdf.rect(14, yPosition - 5, pageWidth - 28, 12, "F");

  pdf.setFontSize(16);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(...conexiaColors.secondary);
  pdf.text("Total Geral", 25, yPosition + 3);
  pdf.text(formatCurrency(data.total), pageWidth - 25, yPosition + 3, {
    align: "right",
  });

  yPosition += 17;

  // Installment Plan Section
  if (data.total > 0) {
    const hasAnnual = produtosAnuaisTotal > 0;
    const hasSemestral = produtosSemestraisTotal > 0;

    let installmentCountLines = 1;
    if (!data.isScheduled) {
      installmentCountLines = (hasAnnual ? 1 : 0) + (hasSemestral ? 1 : 0);
      if (installmentCountLines === 0) installmentCountLines = 1;
    }

    const boxHeight = 48 + (installmentCountLines * 7);

    pdf.setFillColor(248, 249, 250);
    pdf.rect(14, yPosition - 5, pageWidth - 28, boxHeight, "F");

    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(14);
    pdf.setFont("helvetica", "bold");
    pdf.text("Plano de Parcelas", 25, yPosition + 5);

    pdf.setFontSize(10);
    pdf.setFont("helvetica", "normal");
    const nextYear = new Date().getFullYear();
    pdf.text(`Cronograma de pagamento para o ano letivo ${nextYear}`, 25, yPosition + 12);
    pdf.text(
      "Informamos que a entrega dos pedidos está condicionada à ausência de pendências financeiras, aprovação em análise de crédito e ao cumprimento das condições contratuais vigentes.",
      25,
      yPosition + 18,
      { maxWidth: pageWidth - 50 }
    );
    pdf.text(
      "Em caso de dúvidas ou necessidade de regularização, nossa equipe está à disposição para apoiar.",
      25,
      yPosition + 28
    );
    pdf.text("Ajuda Conexia: https://ajuda.conexia.com.br/hc/pt-br/requests/new", 25, yPosition + 34);
    pdf.text("WhatsApp: 0800-345-8000", 25, yPosition + 40);

    pdf.setFontSize(11);
    let installmentY = yPosition + 48;

    if (data.isScheduled) {
      const scheduledInfo = resolveInstallmentInfo(
        data.quantityInstallment,
        undefined,
        6
      );
      const scheduledInstallments = calculateInstallments(
        data.netSubtotal + frete,
        scheduledInfo.calcQtd
      );
      pdf.setFont("helvetica", "bold");
      pdf.text(`Pedido de 2º Semestre - ${scheduledInfo.label}:`, 25, installmentY);
      pdf.text(
        formatCurrency(scheduledInstallments.installmentValue),
        pageWidth - 25,
        installmentY,
        { align: "right" }
      );
      installmentY += 7;
    } else {
      if (hasAnnual) {
        const annualInfo = resolveInstallmentInfo(
          data.annualQuantityInstallment,
          data.quantityInstallment,
          12
        );

        const annualInstallments = calculateInstallments(
          Math.max(0, produtosAnuaisTotal + (totalItensOutros === 0 ? frete : 0) - couponDiscount),
          annualInfo.calcQtd
        );
        pdf.setFont("helvetica", "bold");
        pdf.text(`Pedido Anual - ${annualInfo.label}:`, 25, installmentY);
        pdf.text(
          formatCurrency(annualInstallments.installmentValue),
          pageWidth - 25,
          installmentY,
          { align: "right" }
        );
        installmentY += 7;
      }

      if (hasSemestral) {
        const semestralInfo = resolveInstallmentInfo(
          data.semestralQuantityInstallment,
          hasAnnual ? undefined : data.quantityInstallment,
          6
        );

        const semestralInstallments = calculateInstallments(
          Math.max(0, produtosSemestraisTotal + (!hasAnnual && totalItensOutros === 0 ? frete : 0) - (hasAnnual ? 0 : couponDiscount)),
          semestralInfo.calcQtd
        );
        pdf.setFont("helvetica", "bold");
        pdf.text(`Pedido Semestral - ${semestralInfo.label}:`, 25, installmentY);
        pdf.text(
          formatCurrency(semestralInstallments.installmentValue),
          pageWidth - 25,
          installmentY,
          { align: "right" }
        );
        installmentY += 7;
      }
    }

    yPosition += boxHeight + 5;
  }

  // Cupom por itens: só a identificação do cupom no resumo (os itens cobertos ficam na coluna do extrato).
  if (data.couponCode && (data.couponItems?.length ?? 0) > 0) {
    pdf.setFontSize(11);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...conexiaColors.secondary);
    pdf.text(`Cupom aplicado: ${data.couponCode}`, 25, yPosition + 2);
    pdf.setTextColor(0, 0, 0);
    yPosition += 12;
  }

  // Segment Summary Table
  const segmentGroups = groupItemsBySegmentAndSeriesForPDF(
    data.orderItems.filter((item: OrderItem) => item.quantidade_pedido > 0)
  );

  if (segmentGroups.length > 0) {
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(14);
    pdf.setFont("helvetica", "bold");
    pdf.text("Resumo por Segmento", 25, yPosition);

    const tableData = segmentGroups.map(segment => [
      segment.nome,
      segment.series.length.toString(),
      segment.series.reduce((sum, s) => sum + s.produtos.length, 0).toString(),
      formatCurrency(segment.total),
    ]);

    autoTable(pdf, {
      startY: yPosition + 5,
      head: [["Segmento", "Séries", "Produtos", "Total"]],
      body: tableData,
      theme: "grid",
      headStyles: { fillColor: conexiaColors.secondary },
      margin: { left: 25, right: 25 },
      styles: {
        fontSize: 10,
        overflow: "linebreak",
      },
      columnStyles: {
        0: { cellWidth: "auto" }, // Segmento - largura automática
        1: { cellWidth: 25 }, // Séries - largura fixa
        2: { cellWidth: 25 }, // Produtos - largura fixa
        3: { cellWidth: 35 }, // Total - largura fixa
      },
      tableWidth: "wrap",
    });

    // Adicionar nota sobre pedidos programados
    const tableEndY = pdf.lastAutoTable?.finalY || yPosition + 60;
    pdf.setFontSize(8);
    pdf.setFont("helvetica", "italic");
    pdf.setTextColor(100, 100, 100);
    // pdf.text( "* Os valores exibidos não incluem itens do Pedido Programado (2º Semestre).", 25, tableEndY + 5 );
  }

  // Footer note
  const finalY = pdf.lastAutoTable?.finalY || yPosition + 60;
  pdf.setFillColor(...conexiaColors.primaryBackground);
  pdf.rect(14, finalY + 10, pageWidth - 28, 20, "F");

  pdf.setFontSize(11);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(...conexiaColors.secondary);
  pdf.text("Extrato Detalhado", pageWidth / 2, finalY + 18, {
    align: "center",
  });

  pdf.setFontSize(9);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(100, 100, 100);
  pdf.text("As próximas páginas contêm o detalhamento completo dos produtos", pageWidth / 2, finalY + 23, {
    align: "center",
  });
  pdf.text("organizados por segmento e série para sua conferência.", pageWidth / 2, finalY + 27, { align: "center" });
}

function drawDetailedPageHeader(pdf: ExtendedJsPDF, data: any) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  let yPosition = 20;

  // Page Header
  pdf.addImage(conexiaImage, "PNG", 14, 10, 40, 12);
  yPosition += 12;
  pdf.setFontSize(18);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(0, 0, 0);
  pdf.text("EXTRATO DETALHADO DO PEDIDO", pageWidth / 2, yPosition, {
    align: "center",
  });

  yPosition += 8;
  pdf.setFontSize(14);
  pdf.setTextColor(...conexiaColors.secondary);
  pdf.text(`#${data.orderNumber}`, pageWidth / 2, yPosition, {
    align: "center",
  });

  const getSafeDate = () => {
    const d = new Date();
    if (isNaN(d.getTime()) || d.getFullYear() < 2020) {
      return new Date(Date.now());
    }
    return d;
  };

  const safeNow = getSafeDate();
  const day = String(safeNow.getDate()).padStart(2, '0');
  const month = String(safeNow.getMonth() + 1).padStart(2, '0');
  const year = safeNow.getFullYear();
  const formattedDate = `${day}/${month}/${year}`;

  yPosition += 6;
  pdf.setFontSize(10);
  pdf.setTextColor(100, 100, 100);
  pdf.text(
    `${data.clientName} • ${formattedDate} • Segmento ${data.segmentIndex + 1} de ${data.totalSegments}`,
    pageWidth / 2,
    yPosition,
    { align: "center" }
  );

  return yPosition + 15;
}

function drawSegmentHeader(pdf: ExtendedJsPDF, data: any, yPosition: number, isContinuation: boolean = false) {
  const pageWidth = pdf.internal.pageSize.getWidth();

  pdf.setFillColor(...conexiaColors.secondary);
  pdf.rect(14, yPosition - 5, pageWidth - 28, 20, "F");

  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(16);
  pdf.setFont("helvetica", "bold");
  pdf.text(`${data.segment.nome}`, 25, yPosition + 5);

  pdf.setFontSize(10);
  if (!isContinuation) {
    const seriesCount = data.segment.series.length;
    const productsCount = data.segment.series.reduce((sum: number, s: any) => sum + s.produtos.length, 0);
    pdf.text(
      `${seriesCount} ${seriesCount === 1 ? "série" : "séries"} • ${productsCount} ${productsCount === 1 ? "produto" : "produtos"}`,
      25,
      yPosition + 10
    );

    pdf.text(formatCurrency(data.segment.total), pageWidth - 25, yPosition + 7, {
      align: "right",
    });
  }

  return yPosition + 25;
}

function generateSegmentPage(pdf: ExtendedJsPDF, data: any) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  let yPosition = drawDetailedPageHeader(pdf, data);

  // Segment Header
  yPosition = drawSegmentHeader(pdf, data, yPosition);

  // Separar produtos por tipo de utilização (baseado em UseId do backend)
  // UseId: 1 = Aluno, 2 = Professor
  // Cupom por itens: coluna extra com as unidades cobertas pelo cupom; o Total já vem com o desconto.
  const hasCouponColumn: boolean = (data.couponQtyByName?.size ?? 0) > 0;
  const studentProducts = data.segment.series.map((serie: any) => {
    return serie.produtos.filter((produto: any) => {
      const util = (produto.utilizacao || "").toLowerCase();
      return util !== "professor";
    });
  });

  const teacherProducts = data.segment.series.map((serie: any) => {
    return serie.produtos.filter((produto: any) =>
      (produto.utilizacao || "").toLowerCase() === "professor"
    );
  });

  const hasStudentProducts = studentProducts.some(series => series.length > 0);
  const hasTeacherProducts = teacherProducts.some(series => series.length > 0);

  if (hasStudentProducts) {
    pdf.setFillColor(...conexiaColors.secondary);
    pdf.rect(14, yPosition - 5, pageWidth - 28, 12, "F");

    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(12);
    pdf.setFont("helvetica", "bold");
    pdf.text("Materiais para Aluno", 25, yPosition + 3);

    yPosition += 17;

    // Series Tables
    data.segment.series.forEach((serie: any, serieIndex: number) => {
      // Skip if no student products for this series
      if (studentProducts[serieIndex].length === 0) {
        return;
      }

      // Check if we need a new page
      const requiredSpace = 25 + studentProducts[serieIndex].length * 6; // Space for header + products
      if (yPosition + requiredSpace > 280) {
        pdf.addPage();
        yPosition = drawDetailedPageHeader(pdf, data);
        yPosition = drawSegmentHeader(pdf, data, yPosition, true);

        // Repeat "Materiais para Aluno" header if continuing this section
        pdf.setFillColor(...conexiaColors.secondary);
        pdf.rect(14, yPosition - 5, pageWidth - 28, 12, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "bold");
        pdf.text("Materiais para Aluno", 25, yPosition + 3);
        yPosition += 17;
      }

      const studentTotal = studentProducts[serieIndex].reduce((sum: number, p: any) => sum + p.valorTotal, 0);

      // Serie Header
      pdf.setFillColor(...conexiaColors.primaryBackground);
      pdf.rect(14, yPosition - 3, pageWidth - 28, 12, "F");

      pdf.setTextColor(...conexiaColors.secondary);
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.text(serie.nome, 25, yPosition + 4);
      pdf.text(formatCurrency(studentTotal), pageWidth - 25, yPosition + 4, {
        align: "right",
      });

      yPosition += 15;

      // Products Table - Usando as mesmas nomenclaturas da interface
      const tableData = studentProducts[serieIndex].map((produto: any) => [
        produto.nome,
        produto.quantidade.toString(),
        ...(hasCouponColumn ? [(data.couponQtyByName.get(produto.codigo) ?? 0) > 0 ? String(data.couponQtyByName.get(produto.codigo)) : "-"] : []),
        formatCurrency(produto.valorUnitario),
        formatCurrency(produto.valorTotal),
      ]);

      autoTable(pdf, {
        startY: yPosition,
        head: [["Produto", "Qtd", ...(hasCouponColumn ? ["Coberto por cupom"] : []), "Valor Unit.", "Total"]], // Mesmas nomenclaturas da interface
        body: tableData,
        theme: "striped",
        headStyles: {
          fillColor: conexiaColors.primaryBackground,
          textColor: conexiaColors.secondary,
        },
        styles: {
          fontSize: 9,
        },
        tableWidth: pageWidth - 28,
      });

      yPosition = pdf.lastAutoTable?.finalY || yPosition + 20;
      yPosition += 10;
    });
  }

  if (hasTeacherProducts) {
    // Check if we need a new page or just some space
    if (yPosition + 30 > 280) {
      pdf.addPage();
      yPosition = drawDetailedPageHeader(pdf, data);
      yPosition = drawSegmentHeader(pdf, data, yPosition, true);
    } else {
      yPosition += 10;
    }

    pdf.setFillColor(...conexiaColors.secondary);
    pdf.rect(14, yPosition - 5, pageWidth - 28, 12, "F");

    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(12);
    pdf.setFont("helvetica", "bold");
    pdf.text("Materiais para Professor", 25, yPosition + 3);

    yPosition += 17;

    // Series Tables
    data.segment.series.forEach((serie: any, serieIndex: number) => {
      if (teacherProducts[serieIndex].length === 0) {
        return;
      }

      // Check if we need a new page
      const requiredSpace = 25 + teacherProducts[serieIndex].length * 6;
      if (yPosition + requiredSpace > 280) {
        pdf.addPage();
        yPosition = drawDetailedPageHeader(pdf, data);
        yPosition = drawSegmentHeader(pdf, data, yPosition, true);

        // Repeat "Materiais para Professor" header if continuing this section
        pdf.setFillColor(...conexiaColors.secondary);
        pdf.rect(14, yPosition - 5, pageWidth - 28, 12, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "bold");
        pdf.text("Materiais para Professor", 25, yPosition + 3);
        yPosition += 17;
      }

      const teacherTotal = teacherProducts[serieIndex].reduce((sum: number, p: any) => sum + p.valorTotal, 0);

      // Serie Header
      pdf.setFillColor(...conexiaColors.primaryBackground);
      pdf.rect(14, yPosition - 3, pageWidth - 28, 12, "F");

      pdf.setTextColor(...conexiaColors.secondary);
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.text(serie.nome, 25, yPosition + 4);
      pdf.text(formatCurrency(teacherTotal), pageWidth - 25, yPosition + 4, {
        align: "right",
      });

      yPosition += 15;

      // Products Table - Usando as mesmas nomenclaturas da interface
      const tableData = teacherProducts[serieIndex].map((produto: any) => [
        produto.nome,
        produto.quantidade.toString(),
        ...(hasCouponColumn ? [(data.couponQtyByName.get(produto.codigo) ?? 0) > 0 ? String(data.couponQtyByName.get(produto.codigo)) : "-"] : []),
        formatCurrency(produto.valorUnitario),
        formatCurrency(produto.valorTotal),
      ]);

      autoTable(pdf, {
        startY: yPosition,
        head: [["Produto", "Qtd", ...(hasCouponColumn ? ["Coberto por cupom"] : []), "Valor Unit.", "Total"]], // Mesmas nomenclaturas da interface
        body: tableData,
        theme: "striped",
        headStyles: {
          fillColor: conexiaColors.primaryBackground,
          textColor: conexiaColors.secondary,
        },
        styles: {
          fontSize: 9,
        },
        tableWidth: pageWidth - 28,
      });

      yPosition = pdf.lastAutoTable?.finalY || yPosition + 20;
      yPosition += 10;
    });
  }

  // Page Footer (only on last segment)
  if (data.segmentIndex === data.totalSegments - 1) {
    const footerY = 280;
    pdf.setFillColor(...conexiaColors.primaryBackground);
    pdf.rect(14, footerY - 15, pageWidth - 28, 20, "F");

    pdf.setTextColor(...conexiaColors.secondary);
    pdf.setFontSize(12);
    pdf.setFont("helvetica", "bold");
    pdf.text("Total Geral do Extrato", 25, footerY - 7);

    // Corrigindo para mostrar o total geral correto
    const grandTotal = data.segmentGroups?.reduce((sum: number, seg: any) => sum + seg.total, 0) || data.segment.total;
    pdf.text(formatCurrency(grandTotal), pageWidth - 25, footerY - 7, {
      align: "right",
    });

    pdf.setFontSize(8);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(100, 100, 100);
    pdf.text("Este extrato detalhado complementa o resumo executivo do pedido.", pageWidth / 2, footerY - 2, {
      align: "center",
    });
  }
}
